import { nanoid } from 'nanoid';
import { Edge, MarkerType, Node } from 'reactflow';

import { getGroupLabelHeight } from '@components/plutoflow/GroupNode';
import { defaultPlotNodeStyle } from '@components/plutoflow/PlotNode';

import { StateSetter } from '@contexts/ContextTypes';

import { PostNewNodeParams, UpdateNodeDataType } from '@hooks/useCanvasNodes';

import { CreateObjectiveRunResponse, GetObjectiveRunResponse } from '@models/Objective';

export const aiAnalysisChatWindowNodeStyle = {
    height: 470,
};

const baseGroupNode = { height: 600, width: 450 };
export const baseAiAnalysesNode = { width: 500 };

const childNodePadding = (baseGroupNode.width - defaultPlotNodeStyle.width) / 2;

const calculatePosition = (index: number, parentWidth: number, parentHeight: number) => {
    const horizontalGap = 75;
    const spacing = parentWidth + horizontalGap; // Spacing between nodes
    const x = spacing * (index + 1);
    const y = parentHeight;

    return {
        x,
        y,
    };
};

export const updateRunDataSequence = async ({
    chatWindowNodeByRunUuid,
    runData,
    updateNodeData,
}: {
    chatWindowNodeByRunUuid: Record<string, Node>;
    runData: GetObjectiveRunResponse;
    updateNodeData: UpdateNodeDataType;
}) => {
    if (!runData) return;
    // Update the aiAnalysesChatWindow node with the new data & error status
    const chatWindowNode = chatWindowNodeByRunUuid[runData.uuid];

    if (chatWindowNode) {
        updateNodeData(
            chatWindowNode.id,
            { runData, error: runData?.status === 'failed' || false },
            { skipSelectNode: true },
        );
    }
};

export const createNewRunDataSequence = async ({
    canvasNodes,
    edgeY,
    objectiveId,
    parentNode,
    postNewNode,
    runData,
    setEdges,
    setEdgeY,
}: {
    canvasNodes: Node[];
    edgeY?: number | undefined;
    objectiveId: string;
    parentNode: Node | null;
    postNewNode: (args: PostNewNodeParams) => Promise<Node>;
    runData: GetObjectiveRunResponse | CreateObjectiveRunResponse;
    setEdges: StateSetter<Edge[]>;
    setEdgeY?: StateSetter<number | undefined>;
}) => {
    if (!parentNode || !runData) return;

    const groupNodesInSequence = canvasNodes.filter((n) => n.parentNode === parentNode.id);
    const index = groupNodesInSequence.length;
    const prevGroupNode = groupNodesInSequence.find((n) => n.data?.index === index - 1);

    const width = index === 0 ? baseAiAnalysesNode.width : parentNode.style?.width ? +parentNode.style.width : 0;
    const { x, y } = calculatePosition(index, width, parentNode.style?.height ? +parentNode.style.height : 0);

    // 1. Create group
    const groupLabel = `Analysis ${index + 1} (pending)`;
    const groupLabelHeight = getGroupLabelHeight(groupLabel);
    const newGroupNode = await postNewNode({
        nodeType: 'group',
        nodeStyle: {
            height:
                groupLabelHeight +
                childNodePadding / 2 +
                defaultPlotNodeStyle.height +
                childNodePadding +
                aiAnalysisChatWindowNodeStyle.height +
                childNodePadding +
                childNodePadding / 2,
            width: baseGroupNode.width,
            backgroundColor: 'white',
            borderWidth: 0,
            borderRadius: '1rem',
        },
        nodeData: { label: groupLabel, index, sourceNodeId: parentNode.id },
        parentNode: parentNode.id,
        skipSelectNode: true,
        position: {
            x,
            y,
        },
        deletable: false,
    });

    // Create edge between group and parent/previous node (depending on index)
    const newEdgeState = {
        style: {
            strokeWidth: 2,
            stroke: 'black',
        },
        selectable: true,
        deletable: true,
        type: 'horizontal',
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: 'black',
        },
        label: '',
        sourceHandle: 'right',
        targetHandle: 'left',
        source: index === 0 ? parentNode.id : prevGroupNode?.id || '',
        target: newGroupNode.id,
        id: `edge-${nanoid(8)}`,
        ...(index === 0
            ? {
                  data: { setY: setEdgeY },
              }
            : {
                  data: { y: edgeY },
              }),
    };
    setEdges((prev) => [...prev, newEdgeState]);

    // Create analysis placeholder
    postNewNode({
        nodeData: {
            plotId: null,
            legendIsScrollable: true,
            selectable: false,
        },
        nodeType: 'plot',
        nodeStyle: defaultPlotNodeStyle,
        position: {
            x: childNodePadding, // Relative to group node
            y: y + groupLabelHeight + childNodePadding / 2,
        },
        skipSelectNode: true,
        parentNode: newGroupNode.id,
    });

    // Create chat window
    postNewNode({
        nodeType: 'aiAnalysisChatWindow',
        nodeData: {
            runData,
            index,
            sourceNodeId: parentNode.id,
            objectiveId: objectiveId,
            edgeY,
        },
        nodeStyle: {
            height: aiAnalysisChatWindowNodeStyle.height,
            width: baseGroupNode.width - childNodePadding * 2,
        },
        position: {
            x: childNodePadding, // Relative to group node
            y: y + groupLabelHeight + defaultPlotNodeStyle.height + childNodePadding * 1.5,
        },
        parentNode: newGroupNode.id,
        skipSelectNode: true,
        extent: 'parent',
    });
};
